import { Target } from 'lucide-react';
import type { CarbonTargetDto } from '@siliconx/shared';
import { Card } from '@siliconx/shared/ui/Card';
import { SectionLabel } from '@siliconx/shared/ui/SectionLabel';
import { FyReductionTargetCard } from '../financials/carbon/FyReductionTargetCard';
import {
  useEnvironmentalOverviewQuery,
  useOrgCarbonTarget,
} from '../../hooks/useEnvironmentalOverview';
import { BasisTag } from './BasisTag';

/**
 * FY goal tracking (demo-feedback follow-up to SIL-474/475, prototyped on the
 * SIL-464 branch): a richer view of the org's reduction_percent target than
 * the compact FyReductionTargetCard — a base->target span with the current
 * position, plus a straight-line-pace tick and an ahead/behind read.
 *
 * Falls back to FyReductionTargetCard for absolute-budget targets (no pace
 * story exists for a flat annual budget), and renders nothing when the org
 * has no target — same input resolution as the Fy30TargetCard mount it
 * replaces (org headline target + this tab's PUE-applied Scope 2 annual
 * rate; deliberately never the Scope 3 embodied stock).
 *
 * The linear-pace tick is presentation arithmetic on the customer's own
 * target parameters — not a trajectory model; grid intensity and fleet
 * composition are not projected (consistent with the constant-intensity
 * caveat discipline).
 */
export function FyGoalTrackingCard() {
  const targetsQ = useOrgCarbonTarget();
  const overviewQ = useEnvironmentalOverviewQuery();

  const target: CarbonTargetDto | null = targetsQ.data?.org ?? null;
  if (!target) return null;

  const currentAnnualTco2e = overviewQ.data
    ? overviewQ.data.kpis.scope2Kgco2e.valueKgco2e / 1000
    : null;

  if (
    target.targetType !== 'reduction_percent' ||
    target.baseYear == null ||
    target.baseYearTco2e == null ||
    target.targetYear == null ||
    target.reductionPct == null
  ) {
    return <FyReductionTargetCard target={target} currentAnnualTco2e={currentAnnualTco2e} />;
  }
  if (currentAnnualTco2e == null) return null;

  const g = computeGoalTracking({
    baseYear: target.baseYear,
    baseTco2e: target.baseYearTco2e,
    targetYear: target.targetYear,
    reductionPct: target.reductionPct,
    currentTco2e: currentAnnualTco2e,
    nowYear: new Date().getFullYear(),
  });

  return (
    <Card padding="md" data-testid="fy-goal-tracking">
      <div className="mb-1 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Target className="size-4 text-zinc-400" />
          <SectionLabel>FY{target.targetYear} goal tracking</SectionLabel>
        </div>
        <div className="flex items-center gap-2">
          <span
            className={
              g.aheadOfPaceTco2e >= 0
                ? 'rounded bg-emerald-50 px-1.5 py-0.5 text-xs font-medium text-emerald-700'
                : 'rounded bg-amber-50 px-1.5 py-0.5 text-xs font-medium text-amber-700'
            }
          >
            {g.aheadOfPaceTco2e >= 0 ? 'ahead of' : 'behind'} linear pace by{' '}
            {Math.abs(g.aheadOfPaceTco2e).toFixed(1)} t
          </span>
          <BasisTag basis="derived" />
        </div>
      </div>
      <p className="mb-3 text-xs leading-relaxed text-zinc-400">
        {target.reductionPct}% below FY{target.baseYear} ({target.baseYearTco2e} tCO2e,
        customer-provided) by FY{target.targetYear} — target level {g.targetLevelTco2e.toFixed(0)}{' '}
        tCO2e.
      </p>

      <div className="relative mt-4 h-3 rounded-full bg-zinc-100">
        <div
          className="absolute inset-y-0 left-0 rounded-full bg-emerald-500/80"
          style={{ width: `${(g.progress * 100).toFixed(1)}%` }}
        />
        <div
          className="absolute -inset-y-1 w-0.5 bg-zinc-400"
          style={{ left: `${(g.expected * 100).toFixed(1)}%` }}
          title={`Linear pace: ${(g.expected * 100).toFixed(0)}% by now`}
        />
      </div>
      <div className="mt-1.5 flex justify-between font-mono text-[13px] tabular-nums text-zinc-500">
        <span>
          FY{target.baseYear} · {target.baseYearTco2e} t
        </span>
        <span className="font-semibold text-zinc-800">
          now · {currentAnnualTco2e.toFixed(1)} t ({(g.progress * 100).toFixed(0)}% of reduction)
        </span>
        <span>
          FY{target.targetYear} · {g.targetLevelTco2e.toFixed(0)} t
        </span>
      </div>

      <p className="mt-3 text-xs leading-relaxed text-zinc-400">
        Gray tick = where a straight-line path from FY{target.baseYear} to FY{target.targetYear}{' '}
        would sit today. Annual figures are location-based Scope 2 estimates; the target basis is
        recorded in the methodology sheet.
      </p>
    </Card>
  );
}

export interface GoalTrackingInput {
  baseYear: number;
  baseTco2e: number;
  targetYear: number;
  reductionPct: number;
  currentTco2e: number;
  nowYear: number;
}

/** Pure presentation arithmetic — exported for tests. */
export function computeGoalTracking(input: GoalTrackingInput) {
  const targetLevelTco2e = input.baseTco2e * (1 - input.reductionPct / 100);
  const reductionBudget = input.baseTco2e - targetLevelTco2e;
  const achieved = input.baseTco2e - input.currentTco2e;
  const progress = clamp01(reductionBudget > 0 ? achieved / reductionBudget : 0);
  const expected = clamp01((input.nowYear - input.baseYear) / (input.targetYear - input.baseYear));
  const aheadOfPaceTco2e = achieved - expected * reductionBudget;
  return { targetLevelTco2e, reductionBudget, achieved, progress, expected, aheadOfPaceTco2e };
}

function clamp01(n: number): number {
  return Math.min(1, Math.max(0, n));
}
